<template>
  <div class="c1-cal-wrap" @click.self="$emit('close')">
    <div class="c1-cal">

      <!-- Header -->
      <div class="c1-cal-head">
        <div class="c1-cal-nav">
          <button class="c1-cal-nav-btn" @click="prevDay">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <div class="c1-cal-date-label">
            <span class="c1-cal-day-name">{{ calDayName }}</span>
            <span class="c1-cal-day-val">{{ calDateStr }}</span>
            <span v-if="isToday" class="c1-cal-today-badge">TODAY</span>
          </div>
          <button class="c1-cal-nav-btn" @click="nextDay">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
        </div>
        <div class="c1-cal-head-right">
          <div class="c1-cal-stat">
            <span class="c1-cal-stat-n">{{ dayTasks.length }}</span>
            <span class="c1-cal-stat-lbl">tasks</span>
          </div>
          <button class="c1-cal-close" @click="$emit('close')">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
      </div>

      <!-- Timeline body -->
      <div class="c1-cal-body" ref="calBody">

        <!-- Hour rows -->
        <div class="c1-cal-grid">
          <div v-for="hour in hours" :key="hour" class="c1-cal-hour-row">
            <div class="c1-cal-hour-lbl">{{ formatHour(hour) }}</div>
            <div class="c1-cal-hour-slots">
              <div class="c1-cal-slot" v-for="q in 4" :key="q"></div>
            </div>
          </div>

          <!-- Current time marker -->
          <div v-if="isToday" class="c1-cal-now-line" :style="{ top: nowLineTop + 'px' }">
            <div class="c1-cal-now-dot"></div>
          </div>

          <!-- Task blocks -->
          <div
            v-for="task in dayTasks"
            :key="task.id"
            class="c1-cal-block"
            :style="taskBlockStyle(task)"
            :title="taskBlockTitle(task)"
            @click="$emit('openTask', task)"
          >
            <div class="c1-cal-block-inner">
              <div class="c1-cal-block-title">{{ taskBlockTitle(task) }}</div>
              <div class="c1-cal-block-meta">
                {{ taskStartLabel(task) }} – {{ taskEndLabel(task) }}
                <span v-if="taskGapMin(task) > 0" class="c1-cal-gap-badge">+{{ taskGapMin(task) }}m gap</span>
              </div>
              <div v-if="task.to_user || task.from_user" class="c1-cal-block-user">
                {{ userShort(task.to_user || task.from_user) }}
              </div>
            </div>
            <!-- Safety gap bar at bottom -->
            <div v-if="taskGapMin(task) > 0" class="c1-cal-gap-bar" :style="{ height: gapBarHeight(task) + 'px' }"></div>
          </div>
        </div>

      </div>

      <!-- Footer legend -->
      <div class="c1-cal-foot">
        <div class="c1-cal-legend">
          <div class="c1-cal-leg-item">
            <div class="c1-cal-leg-dot" style="background:#fa8100"></div>
            <span>Active</span>
          </div>
          <div class="c1-cal-leg-item">
            <div class="c1-cal-leg-dot" style="background:#2D9D5E"></div>
            <span>Done</span>
          </div>
          <div class="c1-cal-leg-item">
            <div class="c1-cal-leg-dot" style="background:#4275ec"></div>
            <span>Waiting</span>
          </div>
          <div class="c1-cal-leg-item">
            <div class="c1-cal-leg-dot" style="background:#E05A3A"></div>
            <span>Missed</span>
          </div>
          <div class="c1-cal-leg-item">
            <div class="c1-cal-leg-swatch c1-cal-gap-swatch"></div>
            <span>5-min gap</span>
          </div>
        </div>
        <div class="c1-cal-slot-info">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="width:11px;height:11px"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          15-min slots · 5-min safety gap
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import moment from "moment";

const SLOT_H = 15; // px per 15-minute slot
const DAY_START_HOUR = 7;
const DAY_END_HOUR = 22;
const TYPE_COLORS = ["#fa8100","#7C5CFC","#C75450","#2D9D5E","#1144bc","#D4A017","#E05A3A","#00796b","#827717","#4275ec"];

export default {
  name: "C1TaskCalendar",
  props: {
    tasks: { type: Array, default: () => [] },
    taskDictionaries: { type: Object, default: () => ({}) }
  },
  data() {
    return {
      currentDate: moment().format("YYYY-MM-DD"),
      nowMinutes: this.calcNowMinutes(),
      nowTimer: null
    };
  },
  computed: {
    hours() {
      const h = [];
      for (let i = DAY_START_HOUR; i <= DAY_END_HOUR; i++) h.push(i);
      return h;
    },
    isToday() {
      return this.currentDate === moment().format("YYYY-MM-DD");
    },
    calDayName() {
      return moment(this.currentDate).format("dddd");
    },
    calDateStr() {
      return moment(this.currentDate).format("D MMM YYYY");
    },
    dayTasks() {
      return (this.tasks || []).filter(t => {
        const field = t.start_time || t.start_date || t.created_at;
        if (!field) return false;
        return moment(field).format("YYYY-MM-DD") === this.currentDate;
      });
    },
    nowLineTop() {
      return this.minuteToY(this.nowMinutes);
    }
  },
  mounted() {
    this.nowTimer = setInterval(() => {
      this.nowMinutes = this.calcNowMinutes();
    }, 60000);
    // Scroll to current time or 8am
    this.$nextTick(() => {
      const scrollTo = this.minuteToY(Math.max(this.nowMinutes - 60, (DAY_START_HOUR * 60)));
      if (this.$refs.calBody) this.$refs.calBody.scrollTop = scrollTo;
    });
  },
  beforeDestroy() {
    clearInterval(this.nowTimer);
  },
  methods: {
    calcNowMinutes() {
      const n = moment();
      return n.hours() * 60 + n.minutes();
    },
    prevDay() {
      this.currentDate = moment(this.currentDate).subtract(1, "day").format("YYYY-MM-DD");
    },
    nextDay() {
      this.currentDate = moment(this.currentDate).add(1, "day").format("YYYY-MM-DD");
    },
    formatHour(h) {
      return moment().hour(h).minute(0).format("HH:mm");
    },
    // Convert absolute minutes-of-day to pixel Y offset from DAY_START_HOUR
    minuteToY(absMin) {
      const relMin = absMin - DAY_START_HOUR * 60;
      return Math.max(0, (relMin / 15) * SLOT_H);
    },
    taskStartMoment(task) {
      const f = task.start_time || task.start_date;
      return f ? moment(f) : null;
    },
    taskEndMoment(task) {
      const f = task.end_time || task.due_date || task.end_date;
      return f ? moment(f) : null;
    },
    // Total scheduled minutes (end - start), then actual = total - 5 min gap
    taskGapMin(task) {
      const s = this.taskStartMoment(task);
      const e = this.taskEndMoment(task);
      if (!s || !e) return 0;
      const total = e.diff(s, "minutes");
      return total > 5 ? 5 : 0;
    },
    taskBlockStyle(task) {
      const s = this.taskStartMoment(task);
      const e = this.taskEndMoment(task);
      if (!s) return { display: "none" };
      const startMin = s.hours() * 60 + s.minutes();
      const endMin = e ? (e.hours() * 60 + e.minutes()) : (startMin + 30);
      const durationMin = Math.max(endMin - startMin, 15);
      const top = this.minuteToY(startMin);
      const height = Math.max((durationMin / 15) * SLOT_H, SLOT_H);
      const color = this.taskTypeColor(task);
      return {
        top: top + "px",
        height: height + "px",
        background: color,
        borderColor: color
      };
    },
    gapBarHeight(task) {
      const gap = this.taskGapMin(task);
      return Math.round((gap / 15) * SLOT_H);
    },
    taskTypeColor(task) {
      const types = (this.taskDictionaries && this.taskDictionaries.general && this.taskDictionaries.general.task_types) || [];
      const typeId = task.task_type_id || (task.task_type && task.task_type.id);
      // Status-based override
      if (task.is_completed || task.completed_at) return "#2D9D5E";
      if (task.missedScheduledCall) return "#E05A3A";
      if (task.is_waiting) return "#4275ec";
      const idx = types.findIndex(t => t.id === typeId);
      return TYPE_COLORS[idx % TYPE_COLORS.length] || "#fa8100";
    },
    taskBlockTitle(task) {
      return task.title || task.content_title || this.stripHtml(task.content || "").slice(0, 40) || ("Task #" + task.id);
    },
    taskStartLabel(task) {
      const s = this.taskStartMoment(task);
      return s ? s.format("HH:mm") : "--";
    },
    taskEndLabel(task) {
      const e = this.taskEndMoment(task);
      return e ? e.format("HH:mm") : "--";
    },
    userShort(user) {
      if (!user) return "";
      return (user.full_name || user.username || "").split(" ")[0];
    },
    stripHtml(html) {
      return html.replace(/<[^>]+>/g, "");
    }
  }
};
</script>

<style>
.c1-cal-wrap {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.5);
  z-index: 3100;
  display: flex;
  align-items: stretch;
  justify-content: flex-start;
}
.c1-cal {
  width: 520px;
  max-width: 100vw;
  height: 100vh;
  background: var(--c1-bg, #F7F6F3);
  display: flex;
  flex-direction: column;
  box-shadow: 4px 0 32px rgba(0,0,0,.2);
  animation: c1-cal-in .22s cubic-bezier(.25,.46,.45,.94);
}
@keyframes c1-cal-in {
  from { transform: translateX(-60px); opacity: 0; }
  to   { transform: translateX(0);     opacity: 1; }
}

/* Head */
.c1-cal-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: #fff;
  border-bottom: 1px solid var(--c1-border, #E5E2DC);
  flex-shrink: 0;
}
.c1-cal-nav {
  display: flex;
  align-items: center;
  gap: 10px;
}
.c1-cal-nav-btn {
  width: 28px; height: 28px;
  border-radius: 7px; border: none;
  background: var(--c1-surface, #EDECEA);
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  color: var(--c1-t2, #7A7067);
  transition: background .15s;
}
.c1-cal-nav-btn:hover { background: #E0DDD8; }
.c1-cal-nav-btn svg { width: 14px; height: 14px; }
.c1-cal-date-label { display: flex; align-items: baseline; gap: 7px; }
.c1-cal-day-name { font-size: 14px; font-weight: 700; color: var(--c1-t1, #2C2925); }
.c1-cal-day-val { font-size: 12px; color: var(--c1-t2, #7A7067); }
.c1-cal-today-badge {
  font-size: 9px; font-weight: 800;
  background: #fa8100; color: #fff;
  padding: 2px 6px; border-radius: 10px;
  letter-spacing: .5px;
}
.c1-cal-head-right { display: flex; align-items: center; gap: 12px; }
.c1-cal-stat { text-align: center; }
.c1-cal-stat-n { font-size: 18px; font-weight: 800; color: #fa8100; display: block; line-height: 1; }
.c1-cal-stat-lbl { font-size: 9px; color: var(--c1-t3, #A09E98); font-weight: 600; text-transform: uppercase; letter-spacing: .5px; }
.c1-cal-close {
  width: 28px; height: 28px; border-radius: 7px; border: none;
  background: var(--c1-surface, #EDECEA); cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  color: var(--c1-t2, #7A7067); transition: background .15s;
}
.c1-cal-close:hover { background: #E0DDD8; }
.c1-cal-close svg { width: 14px; height: 14px; }

/* Body / grid */
.c1-cal-body {
  flex: 1;
  overflow-y: auto;
  position: relative;
  padding-bottom: 20px;
}
.c1-cal-grid {
  position: relative;
  margin-left: 48px;
  margin-right: 8px;
  min-height: 100%;
}

/* Hour rows */
.c1-cal-hour-row {
  display: flex;
  position: relative;
  height: 60px; /* 4 slots × 15px */
}
.c1-cal-hour-lbl {
  position: absolute;
  left: -44px;
  top: -7px;
  width: 38px;
  text-align: right;
  font-size: 10px;
  font-weight: 600;
  color: var(--c1-t3, #A09E98);
  font-family: "JetBrains Mono", monospace;
}
.c1-cal-hour-slots {
  flex: 1;
  display: flex;
  flex-direction: column;
  border-top: 1px solid var(--c1-border, #E5E2DC);
}
.c1-cal-slot {
  height: 15px;
  border-top: 1px dashed rgba(0,0,0,.04);
}
.c1-cal-slot:first-child { border-top: none; }

/* Now line */
.c1-cal-now-line {
  position: absolute;
  left: 0; right: 0;
  height: 2px;
  background: #E05A3A;
  z-index: 10;
  pointer-events: none;
}
.c1-cal-now-dot {
  width: 8px; height: 8px;
  border-radius: 50%;
  background: #E05A3A;
  position: absolute;
  left: -4px;
  top: -3px;
}

/* Task blocks */
.c1-cal-block {
  position: absolute;
  left: 2px;
  right: 2px;
  border-radius: 7px;
  border-left: 3px solid;
  overflow: hidden;
  cursor: pointer;
  z-index: 5;
  opacity: .92;
  transition: opacity .15s, transform .15s;
  min-height: 15px;
}
.c1-cal-block:hover { opacity: 1; transform: translateX(1px); }
.c1-cal-block-inner {
  padding: 3px 6px;
  height: calc(100% - 0px);
}
.c1-cal-block-title {
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
}
.c1-cal-block-meta {
  font-size: 9px;
  color: rgba(255,255,255,.8);
  font-family: "JetBrains Mono", monospace;
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 1px;
}
.c1-cal-block-user {
  font-size: 9px;
  color: rgba(255,255,255,.7);
  margin-top: 1px;
}
.c1-cal-gap-badge {
  background: rgba(0,0,0,.2);
  padding: 0 4px;
  border-radius: 4px;
  font-size: 8px;
}
/* Safety gap bar — slightly lighter strip at bottom of block */
.c1-cal-gap-bar {
  position: absolute;
  bottom: 0; left: 0; right: 0;
  background: rgba(255,255,255,.25);
  border-top: 1px dashed rgba(255,255,255,.4);
  pointer-events: none;
}

/* Footer */
.c1-cal-foot {
  flex-shrink: 0;
  border-top: 1px solid var(--c1-border, #E5E2DC);
  background: #fff;
  padding: 10px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.c1-cal-legend {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.c1-cal-leg-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  color: var(--c1-t2, #7A7067);
  font-weight: 500;
}
.c1-cal-leg-dot {
  width: 8px; height: 8px; border-radius: 50%;
}
.c1-cal-leg-swatch {
  width: 14px; height: 8px; border-radius: 2px;
}
.c1-cal-gap-swatch {
  background: repeating-linear-gradient(
    45deg,
    rgba(0,0,0,.1),
    rgba(0,0,0,.1) 2px,
    transparent 2px,
    transparent 4px
  );
  border: 1px solid rgba(0,0,0,.15);
}
.c1-cal-slot-info {
  font-size: 10px;
  color: var(--c1-t3, #A09E98);
  display: flex;
  align-items: center;
  gap: 4px;
  font-weight: 500;
}
</style>
